import { create } from 'zustand';
import { feedbackApi } from '../services/api';
import { ASSIGNMENT_CATEGORIES } from '../components/common/filters/AssignmentCategoryFilter';

// Central initial state for easy reset
const initialState = {
  assignments: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  // Filters
  selectedSession: '',
  selectedSessionStatuses: [],
  selectedReviewTypes: [],
  selectedEmployee: null,
  showCompletedOnly: false,
  showLeftIncompleteOnly: false,
  selectedAssignmentCategory: ASSIGNMENT_CATEGORIES.ALL,
  selectedAssignmentStatuses: [],
  orderBy: 'assignedAt',
  orderDirection: 'desc',
  limit: 30,
  // UI State
  showControls: true,
  selectionMode: false,
  selectedAssignments: [],
  scrollPosition: 0,
  isReturningFromDetails: false,
};

const useFeedbackResultsStore = create((set, get) => {
  // Helper: build API params from current state
  const buildParams = (overrides = {}) => {
    const state = get();
    const params = {
      limit: state.limit,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.pageInfo.endCursor,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
    };

    // Feedback session filter
    const feedbackSessionId = overrides.feedbackSessionId !== undefined
      ? overrides.feedbackSessionId
      : state.selectedSession;
    if (feedbackSessionId) {
      params.feedbackSessionId = feedbackSessionId;
    }

    // Session status filter (array to comma-separated)
    const sessionStatuses = overrides.sessionStatus !== undefined
      ? overrides.sessionStatus
      : state.selectedSessionStatuses;
    if (sessionStatuses && sessionStatuses.length > 0) {
      params.sessionStatus = sessionStatuses.join(',');
    }

    // Review type filter (array to comma-separated)
    const reviewTypes = overrides.reviewType !== undefined
      ? overrides.reviewType
      : state.selectedReviewTypes;
    if (reviewTypes && reviewTypes.length > 0) {
      params.reviewType = reviewTypes.join(',');
    }

    // Employee filter
    const employeeId = overrides.employeeId !== undefined
      ? overrides.employeeId
      : state.selectedEmployee?.id;
    if (employeeId) {
      params.employeeId = employeeId;
    }

    // Completed only filter
    const completedOnly = overrides.completedOnly !== undefined
      ? overrides.completedOnly
      : state.showCompletedOnly;
    if (completedOnly) {
      params.completedOnly = 'true';
    }

    // ADR-014 §10m: frozen-incomplete facet. Orthogonal to the status chips —
    // it narrows whatever they already selected rather than competing with them.
    const leftIncompleteOnly = overrides.leftIncompleteOnly !== undefined
      ? overrides.leftIncompleteOnly
      : state.showLeftIncompleteOnly;
    if (leftIncompleteOnly) {
      params.leftIncompleteOnly = 'true';
    }

    // Assignment category filter
    const assignmentCategory = overrides.assignmentCategory !== undefined
      ? overrides.assignmentCategory
      : state.selectedAssignmentCategory;
    if (assignmentCategory && assignmentCategory !== ASSIGNMENT_CATEGORIES.ALL) {
      params.assignmentCategory = assignmentCategory === ASSIGNMENT_CATEGORIES.INDIVIDUAL ? 'individual' : 'session';
    }

    // Assignment status filter (array to comma-separated)
    const assignmentStatuses = overrides.assignmentStatus !== undefined
      ? overrides.assignmentStatus
      : state.selectedAssignmentStatuses;
    if (assignmentStatuses && assignmentStatuses.length > 0) {
      params.assignmentStatus = assignmentStatuses.join(',');
    }

    return params;
  };

  // Track the current fetch request to handle race conditions
  let currentFetchId = 0;

  // Generic fetcher (reset/append)
  const fetchAssignments = async ({ reset = false, overrides = {} } = {}) => {
    // For reset fetches, always allow (will cancel previous results)
    // For load-more fetches, skip if already loading
    if (!reset && (get().isLoading || get().isLoadingMore)) {
      return null;
    }

    // Increment fetch ID to track this request
    const fetchId = ++currentFetchId;

    if (reset) {
      // Clear assignments and pageInfo immediately to show loading state
      set({
        isLoading: true,
        error: null,
        assignments: [],
        pageInfo: { endCursor: null, hasNextPage: false, totalCount: 0 }
      });
    } else {
      set({ isLoadingMore: true, error: null });
    }

    const params = buildParams(reset ? { ...overrides, cursor: null } : overrides);

    try {
      const response = await feedbackApi.getEmployeesAssignments(params);

      // Ignore response if a newer fetch has been initiated (stale response)
      if (fetchId !== currentFetchId) {
        return null;
      }

      if (response) {
        const newAssignments = reset ? response.items : [...get().assignments, ...response.items];
        const pageInfo = {
          ...response.pageInfo,
          hasNextPage: newAssignments.length < response.pageInfo.totalCount,
        };
        set({
          assignments: newAssignments,
          pageInfo,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false, isLoadingMore: false });
      return null;
    } catch (err) {
      // Ignore error if a newer fetch has been initiated
      if (fetchId !== currentFetchId) {
        return null;
      }
      set({
        error: err.message || 'Failed to load feedback assignments',
        isLoading: false,
        isLoadingMore: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // Data actions
    fetchAssignmentsWithReset: (overrides = {}) => fetchAssignments({ reset: true, overrides }),
    fetchAssignmentsLoadMore: () => fetchAssignments({ reset: false }),

    // Filter setters (with auto-refetch)
    setSelectedSession: (selectedSession) => {
      set({ selectedSession });
      get().fetchAssignmentsWithReset({ feedbackSessionId: selectedSession });
    },

    setSelectedSessionStatuses: (selectedSessionStatuses) => {
      set({ selectedSessionStatuses });
      get().fetchAssignmentsWithReset({ sessionStatus: selectedSessionStatuses });
    },

    toggleSessionStatus: (statusConstant) => {
      const current = get().selectedSessionStatuses;
      const updated = current.includes(statusConstant)
        ? current.filter(s => s !== statusConstant)
        : [...current, statusConstant];
      set({ selectedSessionStatuses: updated });
      get().fetchAssignmentsWithReset({ sessionStatus: updated });
    },

    setSelectedReviewTypes: (selectedReviewTypes) => {
      set({ selectedReviewTypes });
      get().fetchAssignmentsWithReset({ reviewType: selectedReviewTypes });
    },

    toggleReviewType: (reviewTypeConstant) => {
      const current = get().selectedReviewTypes;
      const updated = current.includes(reviewTypeConstant)
        ? current.filter(rt => rt !== reviewTypeConstant)
        : [...current, reviewTypeConstant];
      set({ selectedReviewTypes: updated });
      get().fetchAssignmentsWithReset({ reviewType: updated });
    },

    setSelectedEmployee: (selectedEmployee) => {
      set({ selectedEmployee });
      get().fetchAssignmentsWithReset({ employeeId: selectedEmployee?.id || null });
    },

    setShowCompletedOnly: (showCompletedOnly) => {
      set({ showCompletedOnly });
      get().fetchAssignmentsWithReset({ completedOnly: showCompletedOnly });
    },

    toggleCompletedOnly: () => {
      const updated = !get().showCompletedOnly;
      set({ showCompletedOnly: updated });
      get().fetchAssignmentsWithReset({ completedOnly: updated });
    },

    setShowLeftIncompleteOnly: (showLeftIncompleteOnly) => {
      set({ showLeftIncompleteOnly });
      get().fetchAssignmentsWithReset({ leftIncompleteOnly: showLeftIncompleteOnly });
    },

    toggleLeftIncompleteOnly: () => {
      const updated = !get().showLeftIncompleteOnly;
      set({ showLeftIncompleteOnly: updated });
      get().fetchAssignmentsWithReset({ leftIncompleteOnly: updated });
    },

    setSelectedAssignmentCategory: (selectedAssignmentCategory) => {
      set({ selectedAssignmentCategory });
      get().fetchAssignmentsWithReset({ assignmentCategory: selectedAssignmentCategory });
    },

    setSelectedAssignmentStatuses: (selectedAssignmentStatuses) => {
      set({ selectedAssignmentStatuses });
      get().fetchAssignmentsWithReset({ assignmentStatus: selectedAssignmentStatuses });
    },

    toggleAssignmentStatus: (statusConstant) => {
      const current = get().selectedAssignmentStatuses;
      const updated = current.includes(statusConstant)
        ? current.filter(s => s !== statusConstant)
        : [...current, statusConstant];
      set({ selectedAssignmentStatuses: updated });
      get().fetchAssignmentsWithReset({ assignmentStatus: updated });
    },

    // UI State Actions
    setShowControls: (showControls) => set({ showControls }),
    toggleControls: () => set(state => ({ showControls: !state.showControls })),

    setSelectionMode: (selectionMode) => {
      set({ selectionMode });
      if (!selectionMode) {
        set({ selectedAssignments: [] });
      }
    },
    toggleSelectionMode: () => {
      const newMode = !get().selectionMode;
      set({ selectionMode: newMode });
      if (!newMode) {
        set({ selectedAssignments: [] });
      }
    },

    setSelectedAssignments: (selectedAssignments) => set({ selectedAssignments }),
    clearSelectedAssignments: () => set({ selectedAssignments: [] }),

    setScrollPosition: (scrollPosition) => set({ scrollPosition }),
    setIsReturningFromDetails: (isReturningFromDetails) => set({ isReturningFromDetails }),

    // Reset actions
    resetFilters: () => {
      set({
        selectedSession: '',
        selectedSessionStatuses: [],
        selectedReviewTypes: [],
        selectedEmployee: null,
        showCompletedOnly: false,
        showLeftIncompleteOnly: false,
        selectedAssignmentCategory: ASSIGNMENT_CATEGORIES.ALL,
        selectedAssignmentStatuses: [],
      });
    },

    resetState: () => set({ ...initialState }),

    // Infinite loader helper
    shouldLoadMore: (lastVisibleIndex, threshold = 10) => {
      const { assignments, pageInfo, isLoadingMore } = get();
      return !isLoadingMore &&
             pageInfo.hasNextPage &&
             lastVisibleIndex >= assignments.length - threshold;
    },
  };
});

export default useFeedbackResultsStore;
